import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { API, fetchJson } from "@/lib/api";
import { EVENTS, identify, resetIdentity, track } from "@/lib/analytics";
import { useToast } from "@/components/Toasts";

const AuthCtx = createContext(null);

/** Until the server answers, offer nothing: a Google button on a server without
 *  Google credentials only leads to a 503. */
const NO_PROVIDERS = { password: false, google: false };

/**
 * AuthProvider — user state backed by the httpOnly `session_token` cookie our
 * own backend sets.
 *
 * Two ways in, and the server decides which exist (`/auth/providers`):
 *  - email + password, handled in-page: the cookie arrives on the POST response;
 *  - Google, a full-page redirect through the backend and back.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [providers, setProviders] = useState(NO_PROVIDERS);
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (res.status === 401) {
        setUser(null);
        return;
      }
      if (!res.ok) throw new Error("network");
      const data = await res.json();
      setUser(data);
      // Restoring a session is not a sign-in and gets no event — but it is the
      // moment the analytics session learns whose it is, on every cold load.
      identify(data, { language: i18n.resolvedLanguage });
    } catch (e) {
      // Network / server error — keep the existing user; they may still be signed in.
    } finally {
      setLoading(false);
    }
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Which sign-in methods this deployment has. Asked once: it is server
  // configuration, not per-user state.
  useEffect(() => {
    let cancelled = false;
    fetchJson("/auth/providers")
      .then((p) => {
        if (!cancelled) setProviders({ password: Boolean(p?.password), google: Boolean(p?.google) });
      })
      .catch(() => {
        // Unreachable API: keep the form, hide Google (which cannot work anyway).
        if (!cancelled) setProviders({ password: true, google: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The backend appends ?auth=ok|error when it redirects us back after Google.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const auth = params.get("auth");
    if (!auth) return;
    if (auth === "error") {
      setAuthError(params.get("reason") || "sign_in_failed");
    } else {
      // The full-page round trip through Google leaves no sheet to close and
      // no visible change on this screen — the toast is the only "it worked".
      toast(t("toast.signedIn"));
      // Google's route upserts, so from here a first sign-in and a hundredth
      // look identical; both are reported as `signed_in` rather than guessed at.
      track(EVENTS.SIGNED_IN, { method: "google" });
    }
    params.delete("auth");
    params.delete("reason");
    const query = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (query ? `?${query}` : ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sign in with an email and password. Throws ApiError (with the server's
   * `detail`) so the form can show what went wrong; on success the session
   * cookie is already set and `user` flips truthy, which is what the pending
   * order attach in App.js waits for.
   */
  const loginWithPassword = useCallback(async (email, password) => {
    const me = await fetchJson("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email: String(email).trim(), password }),
    });
    setUser(me);
    setAuthError(null);
    identify(me, { language: i18n.resolvedLanguage });
    track(EVENTS.SIGNED_IN, { method: "password" });
    return me;
  }, [i18n.resolvedLanguage]);

  /** Create an account and sign in, in one step. */
  const register = useCallback(async (email, password, name) => {
    const me = await fetchJson("/auth/register", {
      method: "POST",
      body: JSON.stringify({
        email: String(email).trim(),
        password,
        name: name && name.trim() ? name.trim() : null,
      }),
    });
    setUser(me);
    setAuthError(null);
    identify(me, { language: i18n.resolvedLanguage });
    track(EVENTS.SIGNED_UP, { method: "password" });
    return me;
  }, [i18n.resolvedLanguage]);

  /** Full-page redirect into the server-side Google flow. Only offered when
   *  `providers.google` is true — otherwise the endpoint answers 503. */
  const loginWithGoogle = useCallback(() => {
    const next = encodeURIComponent(window.location.pathname);
    window.location.href = `${API}/auth/google/login?app=guest&next=${next}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch (e) {
      // ignore
    }
    setUser(null);
    track(EVENTS.SIGNED_OUT);
    resetIdentity();
  }, []);

  const deleteAccount = useCallback(async () => {
    try {
      await fetch(`${API}/auth/me`, { method: "DELETE", credentials: "include" });
    } catch (e) {
      // ignore
    }
    setUser(null);
    resetIdentity();
  }, []);

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const value = useMemo(
    () => ({
      user,
      loading,
      authError,
      providers,
      loginWithPassword,
      register,
      loginWithGoogle,
      logout,
      deleteAccount,
      refreshUser,
    }),
    [
      user,
      loading,
      authError,
      providers,
      loginWithPassword,
      register,
      loginWithGoogle,
      logout,
      deleteAccount,
      refreshUser,
    ]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};

export { API };
